/**
 * ScopeCard popover: title, span, crew, and crew actions.
 * Pinned mode: short-click on a ScopeBar day opens a portal above the ScopeBar (same geometry as the
 * former scope hover card), with a close control and {@link useOutsideClick} to dismiss.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { IoClose } from "react-icons/io5";

import { Cog } from "../../../../components/icons/svg/outline";
import useOutsideClick from "../../../../hooks/useOutsideClick";
import CrewCard from "../../shared/CrewCard";
import { PLANNING_TOOLTIP_MESSAGES } from "../../../../config";
import {
  formatShortWeekdayOrdinalLongMonth,
  getCalendarDayCount,
  toLocalDateKey,
} from "../../../../utils/datetime/core";
import { getAspectBlockId, getBlockLabel } from "../model/scheduleBlockSelectors";
import {
  buildCrewOverrideDisplayRows,
  getAssignmentDifference,
} from "../model/scheduleWorkerAssignmentDiff";
import { sameScheduleBlockId } from "../model/scheduleIds";
import { scheduleCrewIdListsEqual } from "../model/scheduleCrewClipboard";
import SkippedDayOverlay from "../../shared/SkippedDayOverlay";

/**
 * Find the WorkScopeAspect row for one block on one calendar day.
 *
 * @param {Object} block WorkScopeAspectsBlock-shaped block.
 * @param {Array<Object>|undefined} aspects Flat aspect list (may be windowed).
 * @param {Date} calendarDay Local calendar day to match.
 * @returns {Object|null} Matching aspect, or null when none exists.
 */
function findAspectForBlockDay(block, aspects, calendarDay) {
  if (!block?.id || !calendarDay || !Array.isArray(aspects)) {
    return null;
  }
  const dayKey = toLocalDateKey(calendarDay);
  return (
    aspects.find(
      (a) =>
        sameScheduleBlockId(getAspectBlockId(a), block.id) &&
        toLocalDateKey(a.date) === dayKey
    ) ?? null
  );
}

/**
 * Crew/details panel for a ScopeCard popover (hover or pinned).
 *
 * @param {Object} props
 * @param {Object|null|undefined} props.block WorkScopeAspectsBlock-shaped; may include `contains_locked` from schedule merge ({@link mergeBlocksContainsLocked}).
 * @param {Map<string|number, { name?: string }>|undefined} props.workScopeById Catalog for {@link getBlockLabel}
 * @param {Array<Object>} [props.workScopeAspects]
 * @param {Date|null|undefined} props.hoverDate Calendar day shown in the header (pinned click or hover)
 * @param {boolean} [props.pinned] When true, dialog semantics (pinned popover from click)
 * @param {() => void} [props.onPinnedClose] Close control when `pinned` (popover dismiss)
 * @param {(day: Date) => void} [props.onOpenDayCrewModal] Opens per-day aspect crew modal (same as bar day click)
 * @param {(day: Date) => void|Promise<void>} [props.onRemoveDayCrewOverride] Clears per-day override (aspect matches block)
 * @param {{ workerIds: number[] }|null} [props.scheduleCrewClipboard]
 * @param {(workerIds: number[]) => void} [props.onCopyScheduleCrew]
 * @param {(payload: { mode: 'block'|'day', blockId: number|string, day?: Date }) => Promise<void>|void} [props.onPasteScheduleCrew]
 * @param {string} [props.surfaceBackgroundClassName] — Tailwind bg class for the title/date header strip only (matches scope bar)
 * @param {string} [props.surfaceBackgroundColor] — CSS background for that header strip when not using a role class (e.g. group pastel hex)
 * @param {(block: Object) => void} [props.onOpenScopeSettings] — Opens Scope Settings modal (pinned ScopeCard)
 * @param {(aspectId: number|string, skippedReason: string) => void} [props.onSkippedReasonChange] — Optimistic skipped reason update.
 * @param {(dateKey: string) => boolean} [props.isScheduleDayLocked] — When set, disables crew edit/revert for locked calendar days.
 * @returns {JSX.Element|null}
 */
export function ScopeCardPanel({
  block,
  workScopeById,
  workScopeAspects = [],
  hoverDate,
  pinned = false,
  onPinnedClose,
  onOpenDayCrewModal,
  onRemoveDayCrewOverride,
  scheduleCrewClipboard,
  onCopyScheduleCrew,
  onPasteScheduleCrew,
  surfaceBackgroundClassName,
  surfaceBackgroundColor,
  onOpenScopeSettings,
  onSkippedReasonChange,
  isScheduleDayLocked = () => false,
}) {
  /**
   * Calendar day shown in the crew header: pinned click day, else block start.
   *
   * @param {void}
   * @returns {Date|null}
   */
  const dayForOverride = useMemo(() => {
    if (!block) {
      return null;
    }
    if (hoverDate instanceof Date) {
      return hoverDate;
    }
    if (block.startDate != null) {
      return new Date(block.startDate);
    }
    return null;
  }, [block, hoverDate]);

  const {
    hasOverride,
    crewRows,
    currentCrewWorkerIds,
    isSkippedDay,
    skippedAspectId,
    skippedReason,
  } = useMemo(() => {
    if (!block) {
      return {
        hasOverride: false,
        crewRows: [],
        currentCrewWorkerIds: [],
        isSkippedDay: false,
        skippedAspectId: null,
        skippedReason: "",
      };
    }
    const blockWorkers = Array.isArray(block.worker_assignment)
      ? block.worker_assignment
      : [];
    const aspects = Array.isArray(workScopeAspects) ? workScopeAspects : [];
    const aspect = dayForOverride
      ? findAspectForBlockDay(block, aspects, dayForOverride)
      : null;
    const numericAspect =
      aspect && /^\d+$/.test(String(aspect.id)) ? aspect : null;
    const override =
      Boolean(numericAspect) &&
      getAssignmentDifference(block, numericAspect, false);
    if (!override) {
      return {
        hasOverride: false,
        crewRows: blockWorkers.map((w) => ({ worker: w, kind: "present" })),
        currentCrewWorkerIds: blockWorkers.map((w) => w.id),
        isSkippedDay: aspect?.skipped === true,
        skippedAspectId: aspect?.id ?? null,
        skippedReason: aspect?.skipped_reason ?? "",
      };
    }
    return {
      hasOverride: true,
      crewRows: buildCrewOverrideDisplayRows(block, numericAspect),
      currentCrewWorkerIds: (numericAspect.worker_assignment ?? []).map(
        (w) => w.id
      ),
      isSkippedDay: numericAspect?.skipped === true,
      skippedAspectId: numericAspect?.id ?? null,
      skippedReason: numericAspect?.skipped_reason ?? "",
    };
  }, [block, workScopeAspects, dayForOverride]);

  /**
   * True when the shown day is dispatch-locked so crew edit/revert stay disabled.
   *
   * @param {void}
   * @returns {boolean}
   */
  const crewEditActionsLocked = useMemo(() => {
    if (!(dayForOverride instanceof Date)) {
      return false;
    }
    return Boolean(isScheduleDayLocked(toLocalDateKey(dayForOverride)));
  }, [dayForOverride, isScheduleDayLocked]);

  const clipSnapshot = scheduleCrewClipboard ?? null;
  const showPasteCrew =
    Boolean(onPasteScheduleCrew) &&
    clipSnapshot != null &&
    Array.isArray(clipSnapshot.workerIds);
  const pasteCrewMatches =
    showPasteCrew &&
    scheduleCrewIdListsEqual(clipSnapshot.workerIds, currentCrewWorkerIds);

  /**
   * True only when this day already has a real per-day crew override (not blank, not still matching block).
   * @param {void}
   * @returns {void}
   */
  const dailyPasteNeedsOverwriteConfirm = useMemo(() => {
    if (!block || !dayForOverride) {
      return false;
    }
    const aspect = findAspectForBlockDay(block, workScopeAspects, dayForOverride);
    const ids = (aspect?.worker_assignment ?? []).map((w) => w.id);
    return Boolean(
      aspect &&
      ids.length > 0 &&
      getAssignmentDifference(block, aspect, false)
    );
  }, [block, dayForOverride, workScopeAspects]);

  if (!block) {
    return null;
  }

  const scopeSettingsDisabled = Boolean(block.contains_locked);
  const scopeSettingsTooltip = scopeSettingsDisabled
    ? PLANNING_TOOLTIP_MESSAGES.scopeSettingsLocked
    : PLANNING_TOOLTIP_MESSAGES.scopeSettings;

  const title = getBlockLabel(block, workScopeById);
  const totalDays = getCalendarDayCount(
    block.startDate,
    block.endDate
  );
  const dayLabel =
    totalDays === 1 ? "1 day" : `${totalDays} days`;
  const crewCardTitle =
    hoverDate instanceof Date
      ? formatShortWeekdayOrdinalLongMonth(hoverDate)
      : "Crew";

  const classBg = surfaceBackgroundClassName?.trim() ?? "";
  const hexBg = surfaceBackgroundColor?.trim() ?? "";
  const hasClassBg = Boolean(classBg);
  const hasHexBg = Boolean(hexBg);
  const rootShellClass =
    "font-inter relative flex max-h-[min(40vh,320px)] w-[min(calc(45vw-2rem),30rem)] flex-col overflow-hidden rounded-lg border border-gray-200 bg-white text-left shadow-lg";

  const headerBarClass = [
    "shrink-0 border-b border-black/10 px-3 py-2.5",
    !hasClassBg && !hasHexBg ? "bg-black/10" : "",
    classBg,
  ]
    .filter(Boolean)
    .join(" ");
  const headerBarStyle =
    hasHexBg && !hasClassBg ? { backgroundColor: hexBg } : undefined;

  const shell = (
    <div
      className={rootShellClass}
      role={pinned ? "dialog" : "tooltip"}
      aria-modal={pinned ? "true" : undefined}
    >
      <div className={headerBarClass} style={headerBarStyle}>
        <div className="flex items-baseline justify-between gap-3">
          <p className="min-w-0 flex-1 text-base font-semibold leading-snug drop-shadow-sm">
            {title}
          </p>
          <div className="flex shrink-0 items-center gap-1">
            <p className="text-sm font-bold leading-snug whitespace-nowrap text-gray-800 drop-shadow-sm">
              {dayLabel} total
            </p>
            {pinned && onOpenScopeSettings ? (
              <span
                title={
                  scopeSettingsDisabled ? scopeSettingsTooltip : undefined
                }
                className={
                  scopeSettingsDisabled ? "inline-flex" : "inline-flex"
                }
              >
                <button
                  type="button"
                  aria-disabled={scopeSettingsDisabled}
                  className={`relative z-[2] shrink-0 rounded p-1 text-gray-500 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 ${
                    scopeSettingsDisabled
                      ? "pointer-events-none opacity-40"
                      : "hover:bg-black/10 hover:text-gray-800"
                  }`}
                  title={
                    scopeSettingsDisabled ? undefined : scopeSettingsTooltip
                  }
                  aria-label={scopeSettingsTooltip}
                  onClick={(e) => {
                    e.stopPropagation();
                    if (scopeSettingsDisabled) {
                      return;
                    }
                    onOpenScopeSettings(block);
                  }}
                >
                  <Cog className="h-5 w-5" aria-hidden />
                </button>
              </span>
            ) : null}
            {pinned && onPinnedClose ? (
              <button
                type="button"
                className="relative z-[2] shrink-0 rounded-full bg-white p-1 text-gray-500 shadow-sm ring-1 ring-black/10 transition-colors hover:bg-gray-100 hover:text-gray-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
                aria-label="Close"
                onClick={(e) => {
                  e.stopPropagation();
                  onPinnedClose();
                }}
              >
                <IoClose className="text-xl" aria-hidden />
              </button>
            ) : null}
          </div>
        </div>
      </div>
      <div className="min-h-0 flex-1 overflow-y-auto px-3 py-2.5">
            <CrewCard
              variant={hasOverride ? "override" : "default"}
              title={crewCardTitle}
              crewRows={crewRows}
              crewEditActionsLocked={crewEditActionsLocked}
              editTooltip={PLANNING_TOOLTIP_MESSAGES.editCrewForDay}
              onEditClick={
                onOpenDayCrewModal && dayForOverride
                  ? (e) => {
                    e.stopPropagation();
                    onOpenDayCrewModal(dayForOverride);
                  }
                  : undefined
              }
              revertTooltip={PLANNING_TOOLTIP_MESSAGES.revertCrewForDay}
              onRevertToDefaultClick={
                hasOverride && onRemoveDayCrewOverride && dayForOverride
                  ? (e) => {
                    e.stopPropagation();
                    void onRemoveDayCrewOverride(dayForOverride);
                  }
                  : undefined
              }
              onCopyCrewClick={
                onCopyScheduleCrew
                  ? (e) => {
                    e.stopPropagation();
                    onCopyScheduleCrew(currentCrewWorkerIds);
                  }
                  : undefined
              }
              showPasteCrew={showPasteCrew}
              pasteCrewDisabled={pasteCrewMatches}
              pasteTargetHasExistingCrew={
                dailyPasteNeedsOverwriteConfirm ||
                currentCrewWorkerIds.length > 0
              }
              onPasteCrewClick={
                onPasteScheduleCrew && dayForOverride
                  ? async (e) => {
                    e.stopPropagation();
                    if (pasteCrewMatches) {
                      return;
                    }
                    await onPasteScheduleCrew({
                      mode: "day",
                      blockId: block.id,
                      day: dayForOverride,
                    });
                  }
                  : undefined
              }
            />
      </div>
      {isSkippedDay ? (
        <SkippedDayOverlay
          aspectId={skippedAspectId}
          reason={skippedReason}
          module="schedule"
          onReasonChange={(nextReason) =>
            onSkippedReasonChange?.(skippedAspectId, nextReason)
          }
        />
      ) : null}
    </div>
  );

  if (!pinned) {
    return shell;
  }

  return (
    <>
      {shell}
      <div
        className="pointer-events-none -mt-px flex w-full shrink-0 justify-center"
        aria-hidden
      >
        <div className="relative h-[12px] w-[12px] shrink-0">
          <div
            className="absolute left-1/2 -top-px h-0 w-0 -translate-x-1/2 border-x-[12px] border-t-[12px] border-x-transparent border-t-white"
          />
        </div>
      </div>
    </>
  );
}

/**
 * Legacy hover positioning: same fixed placement as the former hover card (above bar, centered
 * on bar). Pinned ScopeCard popover stays open until close button or outside click.
 *
 * @param {React.RefObject<HTMLElement|null>} anchorRef Bar root element (for getBoundingClientRect)
 * @param {{ block: Object|null|undefined, workScopeById?: Map<string|number, { name?: string }>, workScopeAspects?: Array<Object>, isDragging: boolean, resolveAnchorLeft?: (day: Date|null, rect: DOMRect) => number|null|undefined, onOpenDayCrewModal?: (day: Date) => void, onRemoveDayCrewOverride?: (day: Date) => void|Promise<void>, scheduleCrewClipboard?: { workerIds: number[] }|null, onCopyScheduleCrew?: (workerIds: number[]) => void, onPasteScheduleCrew?: (payload: { mode: 'block'|'day', blockId: number|string, day?: Date }) => Promise<void>|void, barSurfaceBackgroundClassName?: string, barSurfaceBackgroundColor?: string, onOpenScopeSettings?: () => void, onSkippedReasonChange?: (day: Date, reason: string) => void|Promise<void>, isScheduleDayLocked?: (dateKey: string) => boolean }} options
 * @returns {{ popoverPortal: React.ReactNode, openScopeCard: (day: Date) => void, closeScopeCard: () => void, pinnedDayKey: string|null }}
 */
export function useScopeCardPopover(anchorRef, {
  block,
  workScopeById,
  workScopeAspects,
  isDragging,
  resolveAnchorLeft,
  onOpenDayCrewModal,
  onRemoveDayCrewOverride,
  scheduleCrewClipboard,
  onCopyScheduleCrew,
  onPasteScheduleCrew,
  barSurfaceBackgroundClassName,
  barSurfaceBackgroundColor,
  onOpenScopeSettings,
  onSkippedReasonChange,
  isScheduleDayLocked = () => false,
}) {
  const [open, setOpen] = useState(false);
  const [coords, setCoords] = useState({ top: 0, left: 0 });
  const [panelDay, setPanelDay] = useState(null);
  const popoverRef = useRef(null);

  /**
   * Dismiss the pinned popover and clear the anchored day.
   *
   * @param {void}
   * @returns {void}
   */
  const closeScopeCard = useCallback(() => {
    setOpen(false);
    setPanelDay(null);
  }, []);

  /**
   * Stable wrapper so opening the day-crew modal does not recreate the popover bindings.
   *
   * @param {void}
   * @returns {((day: Date) => void)|undefined}
   */
  const wrappedOnOpenDayCrewModal = useMemo(
    () =>
      onOpenDayCrewModal
        ? (day) => {
          onOpenDayCrewModal(day);
        }
        : undefined,
    [onOpenDayCrewModal]
  );

  /**
   * Stable wrapper for reverting a day override from the pinned card.
   *
   * @param {void}
   * @returns {((day: Date) => void)|undefined}
   */
  const wrappedOnRemoveDayCrewOverride = useMemo(
    () =>
      onRemoveDayCrewOverride
        ? (day) => {
          void onRemoveDayCrewOverride(day);
        }
        : undefined,
    [onRemoveDayCrewOverride]
  );

  /**
   * Stable wrapper for pasting clipboard crew onto the shown day or block.
   *
   * @param {void}
   * @returns {((payload: Object) => Promise<void>)|undefined}
   */
  const wrappedOnPasteScheduleCrew = useMemo(
    () =>
      onPasteScheduleCrew
        ? async (payload) => {
          await onPasteScheduleCrew(payload);
        }
        : undefined,
    [onPasteScheduleCrew]
  );

  useOutsideClick(popoverRef, closeScopeCard, {
    ignoreSelector:
      "[data-schedule-worker-select-modal],[data-schedule-scope-modal]",
  });

  /**
   * Place the portal above the bar, centered on the clicked day when `resolveAnchorLeft` is set.
   *
   * @param {Date|null} [dayForAnchor=null] Day used for column-centered left.
   * @returns {void}
   */
  const updateCoords = useCallback((dayForAnchor = null) => {
    const el = anchorRef.current;
    if (!el) {
      return;
    }
    const rect = el.getBoundingClientRect();
    const resolvedLeft = resolveAnchorLeft?.(dayForAnchor, rect);
    setCoords({
      top: rect.top,
      left:
        Number.isFinite(resolvedLeft)
          ? resolvedLeft
          : rect.left + rect.width / 2,
    });
  }, [anchorRef, resolveAnchorLeft]);

  useEffect(() => {
    if (!open || !block) {
      return undefined;
    }
    updateCoords(panelDay);
    /**
     * Recompute portal coords on scroll/resize so the card stays over the bar.
     * @param {void}
     * @returns {void}
     */
    const onMove = () => updateCoords(panelDay);
    window.addEventListener("scroll", onMove, true);
    window.addEventListener("resize", onMove);
    return () => {
      window.removeEventListener("scroll", onMove, true);
      window.removeEventListener("resize", onMove);
    };
  }, [open, block, panelDay, updateCoords]);

  /**
   * Pin the popover to a clicked bar day (ignored while a drag is active).
   *
   * @param {Date} day Clicked calendar day.
   * @returns {void}
   */
  const openScopeCard = useCallback(
    (day) => {
      if (isDragging || !block || !(day instanceof Date)) {
        return;
      }
      setPanelDay(day);
      updateCoords(day);
      setOpen(true);
    },
    [block, isDragging, updateCoords]
  );

  const show = open && Boolean(block) && panelDay instanceof Date;

  const pinnedDayKey = show ? toLocalDateKey(panelDay) : null;

  const popoverPortal =
    show && typeof document !== "undefined"
      ? createPortal(
        <div
          ref={popoverRef}
          className="fixed z-[10000] pointer-events-auto"
          style={{
            top: coords.top - 8,
            left: coords.left,
            transform: "translate(-50%, -95%)",
          }}
        >
          <div className="relative">
            <ScopeCardPanel
              block={block}
              workScopeById={workScopeById}
              workScopeAspects={workScopeAspects}
              hoverDate={panelDay}
              pinned
              onPinnedClose={closeScopeCard}
              onOpenDayCrewModal={wrappedOnOpenDayCrewModal}
              onRemoveDayCrewOverride={wrappedOnRemoveDayCrewOverride}
              scheduleCrewClipboard={scheduleCrewClipboard}
              onCopyScheduleCrew={onCopyScheduleCrew}
              onPasteScheduleCrew={wrappedOnPasteScheduleCrew}
              surfaceBackgroundClassName={barSurfaceBackgroundClassName}
              surfaceBackgroundColor={barSurfaceBackgroundColor}
              onOpenScopeSettings={onOpenScopeSettings}
              onSkippedReasonChange={onSkippedReasonChange}
              isScheduleDayLocked={isScheduleDayLocked}
            />
          </div>
        </div>,
        document.body
      )
      : null;

  return { popoverPortal, openScopeCard, closeScopeCard, pinnedDayKey };
}
